import "react";
import { instana } from "@crystal/analytics";
import fetch from "@crystal/http/_fetch";
import { isJsonObject, isUrlSearchParams } from "./utils.js";
const DEFAULT_TIMEOUT_MS = 3e4;
class RequestError extends Error {
  constructor(message, body, status) {
    super(message);
    this.body = body;
    this.status = status;
  }
}
async function request(url, options = {}, timeout) {
  const fetchOptions = {
    ...options,
    body: resolveBody(options.body),
    headers: resolveHeaders(options.body, options.headers)
  };
  const urlObj = new URL(url);
  for (const [key, val] of Object.entries(options.query || {})) {
    urlObj.searchParams.append(key, val);
  }
  const response = await fetchWithTimeout(urlObj.href, fetchOptions, timeout);
  return handleResponse(response, url);
}
async function fetchWithTimeout(url, options, timeout = DEFAULT_TIMEOUT_MS) {
  if (!timeout)
    return fetch(url, options);
  const controller = new AbortController();
  const promise = fetch(url, {
    ...options,
    signal: controller.signal
  });
  if (options == null ? void 0 : options.signal)
    options.signal.addEventListener("abort", () => controller.abort());
  const timer = setTimeout(() => controller.abort(), timeout);
  return promise.catch((e) => {
    const err = e;
    if (err.name === "AbortError") {
      return Promise.reject(new RequestError(`Request to '${url}' timed out.`, void 0, 408));
    }
    return Promise.reject(err);
  }).finally(() => clearTimeout(timer));
}
async function handleResponse(response, url) {
  var _a;
  try {
    if (!response.ok) {
      return Promise.reject(
        new RequestError(`Request to ${url} failed.`, await parseResponse(response), response.status)
      );
    }
    try {
      return await parseResponse(response);
    } catch {
      return Promise.reject(new RequestError(`Failed to parse response of ${url}`, void 0, response.status));
    }
  } catch (err) {
    const error = err;
    instana("reportError", error, {
      componentStack: error.message,
      meta: {
        errorId: "APIerror",
        url,
        code: (_a = error.body) == null ? void 0 : _a.code,
        status: response.status
      }
    });
    return Promise.reject(error);
  }
}
async function parseResponse(response) {
  const contentType = response.headers.get("Content-Type");
  if (!contentType) {
    return response.text();
  }
  if (contentType.includes("application/json")) {
    return response.json();
  }
  if (contentType.includes("application/hal+json")) {
    return response.json();
  }
  if (contentType.includes("application/octet-stream")) {
    return response.blob();
  }
  return response.text();
}
function resolveHeaders(body, userHeaders) {
  const headers = new Headers();
  if (isJsonObject(body)) {
    headers.set("Content-Type", "application/json");
  } else if (isUrlSearchParams(body)) {
    headers.set("Content-Type", "application/x-www-form-urlencoded");
  }
  for (const [key, value] of new Headers(userHeaders)) {
    headers.set(key, value);
  }
  return [...headers];
}
function resolveBody(body) {
  if (isUrlSearchParams(body)) {
    return body.toString();
  }
  if (isJsonObject(body)) {
    return JSON.stringify(body);
  }
  return body;
}
export {
  RequestError,
  request
};
//# sourceMappingURL=request.js.map
